// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

/**
 * Derives a cache-busting version token from a stored image reference.
 *
 * References are either a bare S3 object key or a full S3 URL (legacy rows store the
 * presign handler's public URL). Either way the tail of the path changes whenever a new
 * image is uploaded, which is exactly what we want the token to track.
 *
 * Uses the last **two** path segments, not one. Upload-derived cover keys look like
 * `tours/{tourId}/{guid}-main.jpg`, where the final segment alone is already unique — but a cover
 * promoted from a gallery photo points at `tours/{tourId}/photos/{photoId}/main.jpg`, whose final
 * segment is the constant `main.jpg`. Keying on that alone would give every promoted cover an
 * identical token, so switching covers would never invalidate the year-long `immutable` cache the
 * BFF serves these bytes with, and the tour card would keep showing the previous image.
 */
export const extractImageVersion = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return 'none';
  }

  let path = trimmed;
  try {
    path = new URL(trimmed).pathname;
  } catch {
    // Already a bare object key.
  }

  const segments = path.split('/').filter(Boolean);
  if (segments.length === 0) {
    return trimmed;
  }

  return segments.slice(-2).join('-');
};
